import { useCallback, useEffect, useState } from "react";
import {
  BlueprintAssignInput,
  BlueprintInfo,
  BlueprintMinimalInfo,
  READABLE_TURN_AROUND,
  TURN_AROUND_CALCULATIONS,
} from "../api/Blueprints";

import {
  useBlueprintAssign,
  useGetBlueprintInfoList,
} from "../api/BlueprintsApi";

import BlueprintBehaviorFlags from "../blueprints/BlueprintBehaviorFlags";
import Button, {
  ButtonColorTypes,
  ButtonTypes,
} from "../shared-components/Button";
import LabeledCheckbox from "../shared-components/LabeledCheckbox";
import LoadingSpinner from "../shared-components/LoadingSpinner";
import { Modal } from "../shared-components/Modal";
import { InformationCircleIcon } from "@heroicons/react/24/outline";
import ReactTooltip from "react-tooltip";
import { sortBy } from "lodash";
import ErrorMessageContainer from "../shared-components/ErrorMessageContainer";
import { REPORT_TYPES } from "../api/Reports";
import { blueprint_tooltip } from "../utils";
import type { CompanyAssignCheckTypeButtonProps } from "./companyAssignCheckTypeButton.types";

export const CompanyAddBlueprintButton = ({
  existingBlueprints,
  blueprintsFromParent,
  companyUUID,
  onSave,
  useButtonOutline = false,
}: CompanyAssignCheckTypeButtonProps) => {
  const [showModal, setShowModal] = useState(false);
  const [selectedBlueprints, setSelectedBlueprints] = useState<string[]>([]);

  const {
    isLoading: isLoadingBlueprintAssign,
    data: blueprintAssignData,
    error: errorBlueprintAssign,
    mutate: assignBlueprint,
    reset: resetBlueprintAssign,
    isSuccess: isSuccessBlueprintAssign,
  } = useBlueprintAssign();

  const {
    isLoading: isLoadingBlueprints,
    data: blueprints,
    mutate: loadBlueprints,
  } = useGetBlueprintInfoList();

  const close = useCallback(() => {
    setShowModal(false);
    resetBlueprintAssign();
  }, [resetBlueprintAssign]);

  useEffect(() => {
    loadBlueprints();
  }, [loadBlueprints]);

  useEffect(() => {
    if (isSuccessBlueprintAssign && blueprintAssignData && blueprints) {
      const blueprintList: BlueprintMinimalInfo[] = blueprintAssignData.map(
        (p) => {
          const foundBlueprint = blueprints.find(
            (s) => s.external_id === p.blueprint_uuid
          );

          return {
            name: foundBlueprint?.name ?? "",
            blueprint_uuid: foundBlueprint?.external_id ?? "",
            report_type: foundBlueprint?.report_type ?? REPORT_TYPES.MISCONDUCT,
          };
        }
      );
      onSave(blueprintList);
      close();
    }
  }, [
    isSuccessBlueprintAssign,
    blueprintAssignData,
    onSave,
    close,
    blueprints,
    selectedBlueprints,
  ]);

  function toggleBlueprintSelection(blueprintUUID: string) {
    let result = [];
    if (selectedBlueprints.some((pivot) => pivot === blueprintUUID)) {
      result = selectedBlueprints.filter((pivot) => pivot !== blueprintUUID);
    } else {
      result = [...selectedBlueprints, blueprintUUID];
    }
    setSelectedBlueprints(result);
    return;
  }

  if (isLoadingBlueprints) return <LoadingSpinner></LoadingSpinner>;

  function isVisible(blueprintUUID?: string) {
    return existingBlueprints.some((pivot) => pivot === blueprintUUID);
  }

  function filterVisibleBlueprints(
    availableBlueprints: BlueprintInfo[] | undefined
  ) {
    let sortedBlueprints = sortBy(
      availableBlueprints,
      (pivot) => pivot.name?.toLocaleLowerCase() ?? ""
    ).filter((pivot) => !isVisible(pivot.external_id));
    if ((blueprintsFromParent ?? []).length > 0) {
      sortedBlueprints = sortedBlueprints.filter((pivot) =>
        blueprintsFromParent?.some(
          (pivotparent) => pivot.external_id === pivotparent
        )
      );
    }
    return sortedBlueprints;
  }

  function showDetails(blueprintUUID?: string) {
    return (
      isVisible(blueprintUUID) ||
      selectedBlueprints.some((pivot) => pivot === blueprintUUID)
    );
  }

  function handleSave() {
    const assign: BlueprintAssignInput[] = [];
    selectedBlueprints.forEach((blueprintUUID) => {
      assign.push({
        blueprint_uuid: blueprintUUID,
        company_uuid: companyUUID,
      });
    });
    assignBlueprint(assign);
  }

  let sortedBlueprints = filterVisibleBlueprints(blueprints);

  function get_turn_around_display(blueprint: BlueprintInfo) {
    const turn_around = blueprint.turn_around;
    let turn_around_display: string = "Unknown";
    if (turn_around === TURN_AROUND_CALCULATIONS.Hour) {
      turn_around_display = `${blueprint.sla_hours} hours`;
    } else if (turn_around === TURN_AROUND_CALCULATIONS.Days) {
      turn_around_display = READABLE_TURN_AROUND.Days;
    } else if (turn_around === TURN_AROUND_CALCULATIONS.SingleBusinessDay) {
      turn_around_display = READABLE_TURN_AROUND.SingleBusinessDay;
    } else if (turn_around === TURN_AROUND_CALCULATIONS.ThreeSixty) {
      turn_around_display = READABLE_TURN_AROUND.ThreeSixty;
    }
    return turn_around_display;
  }

  return (
    <div>
      <Button
        text={"Assign Check Type"}
        type={ButtonTypes.add}
        onClick={() => setShowModal(true)}
        colorType={
          useButtonOutline ? ButtonColorTypes.outline : ButtonColorTypes.primary
        }
      />
      <Modal open={showModal} onClose={close} width={"w-full max-w-4xl p-3"}>
        <div>
          <div className="text-2xl font-bold border-b-2 mb-3 pb-3">
            Add Check Types
          </div>
          <div>
            {sortedBlueprints?.map((blueprint) => (
              <div
                className="rounded-md border-green-600 border-2 p-3 mb-4"
                key={blueprint.external_id}
              >
                <div className="flex gap-4 ">
                  <>
                    {
                      <LabeledCheckbox
                        key={blueprint.external_id}
                        name={""}
                        onChange={() =>
                          toggleBlueprintSelection(blueprint.external_id || "")
                        }
                      ></LabeledCheckbox>
                    }
                  </>
                  <span className="font-bold">{blueprint.name}</span>
                </div>
                {showDetails(blueprint.external_id) && (
                  <div className="mt-2 text-sm border-t-2">
                    <BlueprintBehaviorFlags behaviors={blueprint.behaviors} />
                    <div>
                      <div className="rounded-md bg-gray-50 p-2 font-bold flex">
                        <span>Report SLA Calculations</span>
                        <span data-tip={blueprint_tooltip}>
                          <InformationCircleIcon
                            height={24}
                            width={24}
                            color="grey"
                          />
                        </span>
                        <ReactTooltip
                          className="hover:visible:opacity-1 w-96"
                          delayHide={1000}
                          effect="solid"
                          html={true}
                        />
                      </div>
                      <div className="flex p-2 gap-2 my-2">
                        {blueprint.turn_around ===
                        TURN_AROUND_CALCULATIONS.Hour ? (
                          <div className="flex items-center">
                            <span className="block mr-4 font-medium">
                              Blueprint SLA (in Hours) {blueprint.sla_hours}
                            </span>
                          </div>
                        ) : (
                          <div className="flex items-center">
                            <span className="block mr-4 font-medium">
                              {get_turn_around_display(blueprint)}
                            </span>
                          </div>
                        )}
                      </div>
                      <div className="rounded-md bg-gray-50 p-2 font-bold">
                        Lookback:
                      </div>
                      <div className="flex p-2 gap-2 my-2">
                        <span>
                          {"Lookback period for blueprint (months): "}
                          {blueprint.lookback}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
          {errorBlueprintAssign && (
            <ErrorMessageContainer message="Error assigning blueprints please contact the administrator" />
          )}
          <div className="flex justify-center gap-4 pt-6">
            <Button
              text="Cancel"
              colorType={ButtonColorTypes.cancel}
              onClick={() => {
                close();
              }}
            />
            {!errorBlueprintAssign && !isLoadingBlueprintAssign && (
              <Button
                text="Save"
                onClick={() => {
                  handleSave();
                }}
              />
            )}
            {isLoadingBlueprintAssign && <LoadingSpinner />}
          </div>
        </div>
      </Modal>
    </div>
  );
};
